import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import ContentWrapper from '../components/contentwrapper/ContentWrapper';
import { Link } from 'react-router-dom';
import { fetchAllDataAPI, addFormData } from '../utils/api';
import { toast } from 'react-toastify';
import { FaFilter, FaRedo } from 'react-icons/fa';
import { CiExport } from 'react-icons/ci';
import CsvDownloader from 'react-csv-downloader';
import {
    MdLocalShipping, MdDoneAll, MdCancel,
    MdPendingActions, MdNewLabel, MdCalendarToday,
    MdLayers, MdCalendarMonth, MdEventRepeat, MdDonutLarge
} from 'react-icons/md';
import withAuth from '../utils/withAuth';

const STATUS_CONFIG = {
    'Delivered': { color: '#0e7c52', bg: '#e8f5e9' },
    'RTO Delivered': { color: '#597e0d', bg: '#f1f8e9' },
    'In Transit': { color: '#1565c0', bg: '#e3f2fd' },
    'Out For Delivery': { color: '#00838f', bg: '#e0f7fa' },
    'Cancelled': { color: '#c62828', bg: '#ffebee' },
    'Failed Delivery': { color: '#c62828', bg: '#ffebee' },
    'NEW': { color: '#6a1b9a', bg: '#f3e5f5' },
    'Pickup Booked': { color: '#4e9191', bg: '#e0f4f4' },
    'default': { color: '#555', bg: '#f5f5f5' },
};

const TABS = [
    { key: 'all', label: 'All', icon: <MdLocalShipping />, color: '#4e9191' },
    { key: 'new', label: 'New', icon: <MdNewLabel />, color: '#6a1b9a' },
    { key: 'delivered', label: 'Delivered', icon: <MdDoneAll />, color: '#0e7c52' },
    { key: 'process', label: 'Processing', icon: <MdPendingActions />, color: '#1565c0' },
    { key: 'cancelled', label: 'Cancelled', icon: <MdCancel />, color: '#c62828' },
];

const SkeletonRow = () => (
    <tr>{[...Array(14)].map((_, i) => (
        <td key={i}><div style={{ height: 14, borderRadius: 6, background: 'linear-gradient(90deg,#f0f0f0 25%,#e8e8e8 50%,#f0f0f0 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite' }} /></td>
    ))}</tr>
);

const getStatusStyle = (s) => STATUS_CONFIG[s] || STATUS_CONFIG['default'];

const formatMoney = (value = 0) => {
    const amount = Number(value) || 0;
    return new Intl.NumberFormat('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount);
};

const RahulSkinrage = () => {
    const ref = useRef();
    const [orders, setOrders] = useState([]);
    const [counts, setCounts] = useState({});
    const [isLoading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('all');
    const [filters, setFilters] = useState({});
    const [currentPage, setPage] = useState(1);
    const perPage = 100;

    const filtered = orders.filter(o => {
        if (activeTab === 'new' && !(!o.shipping_status || o.shipping_status === '' || o.shipping_status === 'NEW')) return false;
        if (activeTab === 'delivered' && !['Delivered', 'RTO Delivered'].includes(o.shipping_status)) return false;
        if (activeTab === 'process' && !['In Transit', 'Out For Delivery', 'Pickup Booked', 'RTO Initiated', 'Pickup Rescheduled', 'Out For Pickup', 'Delayed', 'In Flight', 'Box Packing', 'Ready To Pack', 'PICKUP SCHEDULED', 'Pickup Generated', 'Picklist Generated'].includes(o.shipping_status)) return false;
        if (activeTab === 'cancelled' && !['Cancelled', 'Failed Delivery', 'Returned'].includes(o.shipping_status)) return false;

        if (filters.cname && !(o.order_name || '').toLowerCase().includes(filters.cname.toLowerCase())) return false;
        if (filters.onumber && !(o.order_number || '').includes(filters.onumber)) return false;
        if (filters.cmobile && !(o.order_mobile || '').includes(filters.cmobile)) return false;
        if (filters.sstatus && o.shipping_status !== filters.sstatus) return false;
        
        if (filters.date_from || filters.date_to) {
            const oDate = o.order_date ? o.order_date.split(' ')[0] : '';
            if (oDate) {
                if (filters.date_from && oDate < filters.date_from) return false;
                if (filters.date_to && oDate > filters.date_to) return false;
            }
        }

        return true;
    });

    const npage = Math.ceil(filtered.length / perPage);
    const records = filtered.slice((currentPage - 1) * perPage, currentPage * perPage);
    const numbers = [...Array(npage + 1).keys()].slice(1);
    const summary = counts.summary || {};
    const summaryCards = [
        { label: 'Today Orders', data: summary.today, icon: <MdCalendarToday />, bg: 'linear-gradient(140deg, #467f7d 0%, #5ca09c 55%, #72b7b0 100%)', accent: '#bde7e3' },
        { label: 'Yesterday Orders', data: summary.yesterday, icon: <MdLayers />, bg: 'linear-gradient(140deg, #087347 0%, #10955d 55%, #25b976 100%)', accent: '#b9f3d2' },
        { label: 'This Months Orders', data: summary.this_month, icon: <MdCalendarMonth />, bg: 'linear-gradient(140deg, #0baab4 0%, #12c5c8 55%, #37dce0 100%)', accent: '#b8fbff' },
        { label: 'Last Months Orders', data: summary.last_month, icon: <MdEventRepeat />, bg: 'linear-gradient(140deg, #b75ed0 0%, #ca72de 55%, #df91ed 100%)', accent: '#f1cdf8' },
        { label: 'Total Order', data: summary.total, icon: <MdDonutLarge />, bg: 'linear-gradient(140deg, #537807 0%, #689607 55%, #82ad17 100%)', accent: '#d9eda4' },
    ];

    useEffect(() => { loadOrders(); loadCounts(); }, []);

    const loadOrders = () => {
        setLoading(true);
        fetchAllDataAPI('sub-agent-rahul-skinrange-orders')
            .then(res => { if (res?.data?.status === 1) setOrders(res.data.orders || []); })
            .catch(() => toast.error('Failed to load orders'))
            .finally(() => setLoading(false));
    };

    const loadCounts = () => {
        fetchAllDataAPI('sub-agent-rahul-skinrange-counts')
            .then(res => { if (res?.data?.status === 1) setCounts(res.data.counts || {}); });
    };

    const handleFilter = (e) => {
        e.preventDefault();
        if (Object.keys(filters).some(k => filters[k])) {
            setLoading(true);
            addFormData('sub-agent-rahul-skinrange-filter', filters)
                .then(res => { if (res?.data?.status === 1) { setOrders(res.data.orders || []); setPage(1); } })
                .catch(() => toast.error('Filter failed'))
                .finally(() => setLoading(false));
        } else {
            loadOrders();
        }
    };

    const reset = () => { ref.current.reset(); setFilters({}); loadOrders(); };
    const handleInput = e => setFilters({ ...filters, [e.target.name]: e.target.value });

    const csvCols = [
        { id: 'order_number', displayName: 'Order No' },
        { id: 'order_name', displayName: 'Name' },
        { id: 'order_mobile', displayName: 'Mobile' },
        { id: 'state', displayName: 'State' },
        { id: 'city', displayName: 'City' },
        { id: 'shipping_status', displayName: 'Status' },
        { id: 'awb_number', displayName: 'AWB' },
        { id: 'ocourier_name', displayName: 'Courier' },
        { id: 'order_confirm_status', displayName: 'Confirm Status' },
        { id: 'created_at', displayName: 'Order Date' },
    ];

    return (
        <div className="misDashboard">
            <Helmet><title>SATKARTAR::SKINRANGE ORDERS</title></Helmet>
            <style>{`
                @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
                @keyframes fadeInUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
                .sk-orders-page {
                    animation: fadeInUp 0.4s ease;
                    overflow-x: hidden; 
                }

                /* Hero */
                .sk-hero {
                    background: linear-gradient(135deg, #0d4f4f 0%, #4e9191 100%);
                    border-radius: 16px; padding: 20px 24px; color: #fff;
                    margin-bottom: 20px; box-shadow: 0 8px 24px rgba(78,145,145,0.25);
                    display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px;
                }
                .rahul-summary-grid {
                    display: grid;
                    grid-template-columns: repeat(5, minmax(150px, 1fr));
                    gap: 14px;
                    margin-bottom: 22px;
                }
                .rahul-summary-card {
                    position: relative;
                    min-height: 142px;
                    border-radius: 12px;
                    color: #fff;
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                    box-shadow: 0 18px 36px rgba(23, 34, 42, 0.18), inset 0 1px 0 rgba(255,255,255,0.28);
                    padding: 15px 16px 13px;
                    overflow: hidden;
                    isolation: isolate;
                    transition: transform 0.2s ease, box-shadow 0.2s ease;
                }
                .rahul-summary-card::before {
                    content: '';
                    position: absolute;
                    inset: 0;
                    background:
                        radial-gradient(circle at 18% 18%, rgba(255,255,255,0.24), transparent 30%),
                        linear-gradient(120deg, rgba(255,255,255,0.18), rgba(255,255,255,0.04) 42%, rgba(0,0,0,0.08));
                    z-index: -1;
                }

                .rahul-summary-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 24px 44px rgba(23, 34, 42, 0.25), inset 0 1px 0 rgba(255,255,255,0.32);
                }
                .rahul-summary-card svg {
                    font-size: 21px;
                    margin-bottom: 0;
                }
                .rahul-summary-top {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    width: 100%;
                }
                .rahul-summary-icon {
                    width: 40px;
                    height: 40px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 12px;
                    background: rgba(255,255,255,0.16);
                    border: 1px solid rgba(255,255,255,0.26);
                    box-shadow: inset 0 1px 0 rgba(255,255,255,0.2), 0 8px 18px rgba(0,0,0,0.12);
                    backdrop-filter: blur(6px);
                }
                .rahul-summary-chip {
                    height: 22px;
                    min-width: 22px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 999px;
                    padding: 0 8px;
                    font-size: 10px;
                    font-weight: 900;
                    color: rgba(255,255,255,0.94);
                    background: rgba(0,0,0,0.12);
                    border: 1px solid rgba(255,255,255,0.2);
                }
                .rahul-summary-body {
                    width: 100%;
                    text-align: left;
                    margin-top: 12px;
                }
                .rahul-summary-label {
                    font-size: 13px;
                    line-height: 1.2;
                    font-weight: 800;
                    margin-bottom: 6px;
                    opacity: 0.92;
                    text-shadow: 0 1px 2px rgba(0,0,0,0.1);
                }
                .rahul-summary-amount {
                    font-size: 23px;
                    line-height: 1.15;
                    font-weight: 900;
                    letter-spacing: 0;
                    word-break: break-word;
                    text-shadow: 0 2px 10px rgba(0,0,0,0.14);
                }
                .rahul-summary-count {
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;
                    align-self: flex-start;
                    font-size: 12px;
                    font-weight: 800;
                    margin-top: 12px;
                    padding: 4px 10px;
                    border-radius: 999px;
                    background: rgba(255,255,255,0.17);
                    border: 1px solid rgba(255,255,255,0.24);
                    backdrop-filter: blur(6px);
                }
                @media (max-width: 1199px) {
                    .rahul-summary-grid { grid-template-columns: repeat(3, minmax(150px, 1fr)); }
                }
                @media (max-width: 767px) {
                    .rahul-summary-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
                    .rahul-summary-card { min-height: 128px; padding: 13px; }
                    .rahul-summary-label { font-size: 12px; }
                    .rahul-summary-amount { font-size: 18px; }
                }

                /* Tabs */
                .sk-tabs { display: flex; gap: 10px; flex-wrap: wrap; margin-bottom: 18px; }
                .sk-tab {
                    display: flex; align-items: center; gap: 8px;
                    padding: 9px 18px; border-radius: 50px; border: 2px solid #eee;
                    background: #fff; cursor: pointer; font-size: 13px; font-weight: 600;
                    transition: all 0.2s; box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                }
                .sk-tab:hover { transform: translateY(-2px); box-shadow: 0 4px 14px rgba(0,0,0,0.1); }
                .sk-tab.active { color: #fff !important; border-color: transparent; }
                .sk-tab-count { background: rgba(255,255,255,0.25); padding: 1px 8px; border-radius: 20px; font-size: 11px; font-weight: 700; }
                .sk-tab:not(.active) .sk-tab-count { background: #f0f0f0; color: #555; }

                /* Filter */
                .sk-filter {
                    background: #fff;
                    border-radius: 16px;
                    padding: 18px 20px;
                    margin-bottom: 18px;
                    box-shadow: 0 4px 20px rgba(0,0,0,0.06);
                    overflow: hidden; 
                }
                .sk-filter label { font-size: 11px; font-weight: 600; color: #888; text-transform: uppercase; letter-spacing: 0.5px; display: block; margin-bottom: 5px; }
                .sk-filter .form-control { border-radius: 10px; border: 1.5px solid #eee; font-size: 13px; padding: 8px 12px; }
                .sk-filter .form-control:focus { border-color: #4e9191; box-shadow: 0 0 0 3px rgba(78,145,145,0.1); outline: none; }

                /* Table */
                .sk-table-card {
                    background: #fff;
                    border-radius: 16px;
                    box-shadow: 0 4px 20px rgba(0,0,0,0.06);
                    overflow: hidden;
                    margin-bottom: 50px;
                }.sk-table-card 
                .table-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; }
                .sk-table-card table { margin: 0; min-width: 1200px; }
                .sk-table-card thead th { background: #f8f9fa; font-size: 11px; font-weight: 700; color: #666; text-transform: uppercase; letter-spacing: 0.5px; padding: 13px 12px; border-bottom: 2px solid #eee; white-space: nowrap; }
                .sk-table-card tbody tr:hover { background: #f8fffe; }
                .sk-table-card tbody td { font-size: 13px; color: #444; padding: 11px 12px; vertical-align: middle; white-space: nowrap; }

                /* Pagination */
                .round-pagination .page-item.actives .page-link { background: #4e9191 !important; border-color: #4e9191 !important; color: #fff !important; border-radius: 8px !important; }
                .round-pagination .page-item .page-link { background: #fff; border: 1.5px solid #eee; color: #555; border-radius: 8px !important; margin: 0 2px; font-size: 12px; font-weight: 600; padding: 5px 12px; box-shadow: none !important; }
                .round-pagination .page-item .page-link:hover { background: #4e9191 !important; color: #fff !important; }
            `}</style>

            <ContentWrapper>
                <div className="sk-orders-page">

                    {/* Breadcrumb */}
                    <div className="page-breadcrumb d-none d-sm-flex align-items-center mb-3">
                        <nav><ol className="breadcrumb mb-0 p-0">
                            <li className="breadcrumb-item"><Link to="/dashboard">Home</Link></li>
                            <li className="breadcrumb-item">Rahul Skinrange Orders</li>
                        </ol></nav>
                    </div>

                    {/* Hero */}
                    <div className="sk-hero">
                        <div>
                            <div style={{ fontSize: 11, opacity: 0.7, textTransform: 'uppercase', letterSpacing: 1 }}>Skinrange Platform</div>
                            <div style={{ fontSize: 26, fontWeight: 800 }}>{orders.length} Orders</div>
                            <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>Center ID: 99</div>
                        </div>
                        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: 11, opacity: 0.7 }}>Delivered</div>
                                <div style={{ fontSize: 22, fontWeight: 800 }}>{counts.delivered || 0}</div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: 11, opacity: 0.7 }}>Processing</div>
                                <div style={{ fontSize: 22, fontWeight: 800 }}>{counts.process || 0}</div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: 11, opacity: 0.7 }}>Cancelled</div>
                                <div style={{ fontSize: 22, fontWeight: 800 }}>{counts.cancelled || 0}</div>
                            </div>
                        </div>
                    </div>

                    <div className="rahul-summary-grid">
                        {summaryCards.map(card => (
                            <div key={card.label} className="rahul-summary-card" style={{ background: card.bg, '--accent': card.accent }}>
                                <div className="rahul-summary-top">
                                    <span className="rahul-summary-icon">{card.icon}</span>
                                    <span className="rahul-summary-chip">INR</span>
                                </div>
                                <div className="rahul-summary-body">
                                    <div className="rahul-summary-label">{card.label}</div>
                                    <div className="rahul-summary-amount">{'\u20B9'} {formatMoney(card.data?.amount)}</div>
                                </div>
                                <div className="rahul-summary-count">{'\u21C4'} {card.data?.count || 0}</div>
                            </div>
                        ))}
                    </div>

                    {/* Tabs */}
                    <div className="sk-tabs">
                        {TABS.map(tab => (
                            <div key={tab.key}
                                className={`sk-tab ${activeTab === tab.key ? 'active' : ''}`}
                                style={activeTab === tab.key ? { background: tab.color, color: '#fff' } : { color: tab.color }}
                                onClick={() => { setActiveTab(tab.key); setPage(1); }}>
                                {tab.icon} {tab.label}
                                <span className="sk-tab-count">{counts[tab.key] ?? '—'}</span>
                            </div>
                        ))}
                    </div>

                    {/* Filter */}
                    <div className="sk-filter">
                        <form ref={ref} onSubmit={handleFilter}>
                            {/* Filter row - fix col sizes to fit inside card */}
                            <div className="row g-2 align-items-end">
                                <div className="col-6 col-lg-2">
                                    <label>Search By Name</label>
                                    <input name="cname" className="form-control" type="text" placeholder="Customer name" onChange={handleInput} />
                                </div>
                                <div className="col-6 col-lg-2">
                                    <label>Order Number</label>
                                    <input name="onumber" className="form-control" type="text" placeholder="Order number" onChange={handleInput} />
                                </div>
                                <div className="col-6 col-lg-2">
                                    <label>Mobile Number</label>
                                    <input name="cmobile" className="form-control" type="text" placeholder="Mobile number" onChange={handleInput} />
                                </div>
                                <div className="col-6 col-lg-2">
                                    <label>Order Status</label>
                                    <select name="sstatus" className="form-control" onChange={handleInput}>
                                        <option value="">All Statuses</option>
                                        {['NEW', 'Pickup Booked', 'In Transit', 'Out For Delivery', 'Delivered',
                                            'RTO Initiated', 'RTO Delivered', 'Cancelled', 'Failed Delivery', 'Returned'].map(s => (
                                                <option key={s} value={s}>{s}</option>
                                            ))}
                                    </select>
                                </div>
                                <div className="col-6 col-lg-2">
                                    <label>Date From</label>
                                    <input name="date_from" className="form-control" type="date" onChange={handleInput} />
                                </div>
                                <div className="col-6 col-lg-2">
                                    <label>Date To</label>
                                    <input name="date_to" className="form-control" type="date" onChange={handleInput} />
                                </div>

                                {/* Buttons on new row — full width, stays inside card */}
                                <div className="col-12">
                                    <div className="d-flex gap-2 align-items-center flex-wrap" style={{ paddingTop: 8 }}>
                                        <button type="submit" className="btn btn-sm"
                                            style={{ background: '#4e9191', color: '#fff', borderRadius: 8, fontWeight: 600, fontSize: 12, padding: '7px 18px' }}>
                                            <FaFilter style={{ marginRight: 4, fontSize: 10 }} /> Filter
                                        </button>
                                        <button type="button" onClick={reset} className="btn btn-sm"
                                            style={{ background: '#fff3cd', color: '#856404', borderRadius: 8, fontWeight: 600, fontSize: 12, padding: '7px 14px' }}>
                                            <FaRedo style={{ marginRight: 4, fontSize: 11 }} /> Reset
                                        </button>
                                        <CsvDownloader filename="Skinrange_Orders" extension=".csv" datas={orders} columns={csvCols}>
                                            <button type="button" className="btn btn-sm"
                                                style={{ background: '#e3f2fd', color: '#1565c0', borderRadius: 8, fontWeight: 600, fontSize: 12, padding: '7px 14px' }}>
                                                <CiExport style={{ marginRight: 4, fontSize: 14 }} /> Export
                                            </button>
                                        </CsvDownloader>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>

                    {/* Table */}
                    <div className="sk-table-card">
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
                            <span style={{ fontSize: 13, color: '#666', fontWeight: 500 }}>
                                Showing <strong>{records.length}</strong> of <strong>{filtered.length}</strong> orders
                            </span>
                            <span style={{ fontSize: 13, color: '#666' }}>Page <strong>{currentPage}</strong> of <strong>{npage || 1}</strong></span>
                        </div>

                        <div className="table-scroll">
                            <table className="table table-hover mb-0">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Order No.</th>
                                        <th>AWB Number</th>
                                        <th>Product</th>
                                        <th>Name</th>
                                        <th>Mobile</th>
                                        <th>State</th>
                                        <th>City</th>
                                        <th>Pincode</th>
                                        <th>Courier</th>
                                        <th>Dispatch Date</th>
                                        <th>Confirm Status</th>
                                        <th>Shipping Status</th>
                                        <th>Order Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isLoading
                                        ? [...Array(10)].map((_, i) => <SkeletonRow key={i} />)
                                        : records.length === 0
                                            ? <tr>
                                                <td colSpan={13} className="text-center py-5" style={{ color: '#bbb' }}>No orders found</td></tr>
                                            : records.map((o, i) => {
                                                const ss = getStatusStyle(o.shipping_status);
                                                return (
                                                    <tr key={i}>
                                                        <td style={{ color: '#aaa', fontSize: 12 }}>{(currentPage - 1) * perPage + i + 1}</td>
                                                        <td>
                                                            <span style={{ fontSize: 12, fontWeight: 700, color: '#4e9191', fontFamily: 'monospace' }}>
                                                                #{o.order_number}
                                                            </span>
                                                        </td>
                                                        <td style={{ fontFamily: 'monospace', fontSize: 12, color: '#888' }}>
                                                            {o.awb_number || '—'}
                                                        </td>
                                                        <td>
                                                            {o.products && Array.isArray(o.products) ? o.products.map((product, i) => (
                                                                <div key={i}>
                                                                    <span style={{ background: 'rgb(20, 170, 230)', color: '#fff', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600, gap: 4, display: 'inline-flex', alignItems: 'center', marginBottom: 4 }}>
                                                                        {product.product_name} x {product.product_quantity}
                                                                    </span>
                                                                </div>
                                                            )) : '—'}
                                                        </td>
                                                        <td style={{ fontWeight: 500 }}>{o.order_name}</td>
                                                        <td>{o.order_mobile}</td>
                                                        <td>{o.order_state}</td>
                                                        <td>{o.order_city}</td>
                                                        <td>{o.order_pincode}</td>
                                                        <td style={{ fontSize: 12 }}>{o.ocourier_name || '—'}</td>
                                                        <td style={{ fontSize: 12 }}>{o.dispatch_date || '—'}</td>
                                                        <td>
                                                            <span style={{
                                                                background: o.order_confirm_status === 'Confirm Order' ? '#e8f5e9' : '#fff3e0',
                                                                color: o.order_confirm_status === 'Confirm Order' ? '#0e7c52' : '#e65100',
                                                                padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600
                                                            }}>
                                                                {o.order_confirm_status || '—'}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span style={{ background: ss.bg, color: ss.color, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
                                                                {o.shipping_status || 'New'}
                                                            </span>
                                                        </td>
                                                        <td style={{ fontSize: 12, color: '#888' }}>
                                                            {o.order_date ? o.order_date.split(' ')[0] : '—'}
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                    }
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        <div className="d-flex justify-content-end p-3">
                            <nav>
                                <ul className="pagination round-pagination mb-0">
                                    <li className="page-item">
                                        <a href="#" className="page-link" onClick={e => { e.preventDefault(); if (currentPage > 1) setPage(currentPage - 1); }}>← Prev</a>
                                    </li>
                                    {numbers.slice(Math.max(0, currentPage - 2), Math.max(5, currentPage + 3)).map((n, i) => (
                                        <li key={i} className={`page-item ${currentPage === n ? 'actives' : ''}`}>
                                            <a href="#" className="page-link" onClick={e => { e.preventDefault(); setPage(n); }}>{n}</a>
                                        </li>
                                    ))}
                                    <li className="page-item">
                                        <a href="#" className="page-link" onClick={e => { e.preventDefault(); if (currentPage < npage) setPage(currentPage + 1); }}>Next →</a>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                    </div>
                </div>
            </ContentWrapper>
        </div>
    );
};

export default withAuth(RahulSkinrage);
